import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { format, addDays, isBefore, isAfter, addMinutes } from "date-fns";

interface BookingSectionProps {
  onContactClick: () => void;
}

// Available time slots
const TIME_SLOTS = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", 
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM"
];

// Service types
const SERVICE_TYPES = [
  "Cable Railing",
  "Indoor Railing",
  "Ornamental Work",
  "Outdoor Railing",
  "Spiral Stairs",
  "Stainless Steel",
  "Steel Fabrication"
];

// Form schema
const bookingFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(10, { message: "Please enter a valid phone number" }),
  date: z.date({ required_error: "Please select a date" }),
  time: z.string({ required_error: "Please select a time slot" }),
  service: z.string({ required_error: "Please select a service type" }),
  message: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingFormSchema>;

const BookingSection = ({ onContactClick }: BookingSectionProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]); 

  // Fetch unavailable dates
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      try {
        const response = await fetch('/api/unavailable-dates');
        if (!response.ok) {
          throw new Error('Failed to fetch unavailable dates');
        }

        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          // Convert string dates to Date objects
          const dates = data.data.map((item: { date: string }) => 
            new Date(item.date)
          );
          setUnavailableDates(dates);
        }
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
      }
    };

    fetchUnavailableDates();
  }, []);
  const [unavailableSlots, setUnavailableSlots] = useState<string[]>([]); // In a real app, this would be fetched based on the selected date

  // Form
  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const selectedDate = form.watch("date");

  // Simulate fetching unavailable time slots when date changes
  useEffect(() => {
    if (selectedDate) {
      // In a real app, you would make an API call here to fetch unavailable slots for this date
      // For now, we'll randomly block some time slots
      const randomlyBlockedSlots = TIME_SLOTS.filter(() => Math.random() > 0.7);
      setUnavailableSlots(randomlyBlockedSlots);
    }
  }, [selectedDate]);

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.2,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Function to check if a date should be disabled
  const isDateDisabled = (date: Date) => {
    // Disable past dates
    if (isBefore(date, new Date())) {
      return true;
    }

    // Disable weekends
    const day = date.getDay();
    if (day === 0 || day === 6) {
      return true;
    }

    // Disable specific unavailable dates
    return unavailableDates.some((unavailableDate) => 
      unavailableDate.getDate() === date.getDate() && 
      unavailableDate.getMonth() === date.getMonth() && 
      unavailableDate.getFullYear() === date.getFullYear()
    );
  };

  // On form submit
  const onSubmit = async (values: BookingFormValues) => {
    setIsSubmitting(true);

    try {
      // Format the data to be sent to the server
      const formattedDate = format(values.date, "MMMM d, yyyy");
      const bookingData = {
        ...values,
        formattedDate,
      };

      console.log("Booking Form Submitted:", bookingData);

      // Make the API call to submit booking
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit booking request');
      }

      // Show success message
      toast({
        title: "Booking Request Submitted",
        description: `We have received your booking request for ${formattedDate} at ${values.time}. We will contact you shortly to confirm your appointment.`,
      });

      // Reset form
      form.reset();
    } catch (error) {
      console.error("Error submitting booking form:", error);
      toast({
        title: "Error",
        description: "There was an error submitting your booking request. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="booking" 
      className="py-20 bg-gradient-to-b from-zinc-900 to-black text-white"
      ref={sectionRef}
    >
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-4 text-white">Book Your Service</h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-lg max-w-2xl mx-auto text-gray-300">
            Schedule a consultation or book our welding services online. Choose your preferred date and time, and we'll confirm your appointment shortly.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            className="grid md:grid-cols-2 gap-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-zinc-800 p-6 rounded-lg shadow-xl">
              <h3 className="text-xl font-semibold mb-4 text-white">Select Date & Time</h3>

              <div className="bg-zinc-900 rounded-lg p-4 mb-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && form.setValue("date", date)}
                  disabled={isDateDisabled}
                  className="rounded-lg border-0"
                  modifiers={{
                    booked: unavailableDates,
                  }}
                  modifiersClassNames={{
                    booked: "bg-red-800 text-white opacity-50 cursor-not-allowed",
                  }}
                  fromDate={new Date()}
                  toDate={addDays(new Date(), 60)}
                />
              </div>

              {selectedDate && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium mb-3">
                    Available Time Slots for {format(selectedDate, "MMMM d, yyyy")}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {TIME_SLOTS.map((time) => {
                      const isUnavailable = unavailableSlots.includes(time);
                      return (
                        <button
                          key={time}
                          type="button"
                          className={`rounded-md py-2 px-3 text-sm font-medium transition-colors
                            ${form.watch("time") === time 
                              ? "bg-red-600 text-white" 
                              : isUnavailable 
                                ? "bg-zinc-700 text-gray-500 cursor-not-allowed" 
                                : "bg-zinc-700 text-white hover:bg-zinc-600"
                            }`}
                          onClick={() => !isUnavailable && form.setValue("time", time)}
                          disabled={isUnavailable}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-zinc-800 p-6 rounded-lg shadow-xl">
              <h3 className="text-xl font-semibold mb-4 text-white">Your Information</h3>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white font-medium">Full Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="John Doe" 
                            {...field} 
                            className="bg-zinc-700 border-zinc-600 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white font-medium">Email</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="your@email.com" 
                            type="email" 
                            {...field} 
                            className="bg-zinc-700 border-zinc-600 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white font-medium">Phone Number</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="(123) 456-7890" 
                            {...field} 
                            className="bg-zinc-700 border-zinc-600 text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="service"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white font-medium">Service Type</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="bg-zinc-700 border-zinc-600 text-white">
                              <SelectValue placeholder="Select service type" className="placeholder:text-gray-400" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {SERVICE_TYPES.map((service) => (
                              <SelectItem key={service} value={service}>
                                {service}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white font-medium">Additional Information (Optional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Tell us about your project or any specific requirements" 
                            {...field} 
                            className="bg-zinc-700 border-zinc-600 min-h-[100px] text-white placeholder:text-gray-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="pt-4 flex flex-col space-y-4">
                    <Button 
                      type="submit" 
                      disabled={isSubmitting || !form.formState.isValid || !form.watch("date") || !form.watch("time")}
                      className="bg-transparent hover:bg-white hover:text-black text-white text-lg font-montserrat font-semibold rounded-md transition-colors border-2 border-white items-center justify-center py-6"
                    >
                      {isSubmitting ? "Submitting..." : "Book Appointment"}
                    </Button>

                    <p className="text-sm text-gray-400 text-center">
                      Need assistance? <button 
                        type="button" 
                        onClick={onContactClick}
                        className="text-red-500 hover:text-red-400 underline" 
                      >
                        Contact us directly
                      </button>
                    </p>
                  </div>
                </form>
              </Form>
            </div>
          </motion.div>

          <motion.div
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-2xl font-semibold mb-4 text-white">Why Book Online?</h3>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-zinc-800 p-6 rounded-lg">
                <div className="text-red-500 text-3xl mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2 text-white">Save Time</h4>
                <p className="text-gray-400">Book your appointment anytime, day or night, without waiting on the phone.</p>
              </div>

              <div className="bg-zinc-800 p-6 rounded-lg">
                <div className="text-red-500 text-3xl mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2 text-white">Easy Management</h4>
                <p className="text-gray-400">Receive confirmation emails with all the details about your appointment.</p>
              </div>

              <div className="bg-zinc-800 p-6 rounded-lg">
                <div className="text-red-500 text-3xl mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2 text-white">Personalized Service</h4>
                <p className="text-gray-400">Let us know about your specific needs before the appointment.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;